import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { FaCheck, FaClock, FaFileInvoice, FaHourglassStart, FaTimes } from 'react-icons/fa';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ErrorToast } from '../../../utils/toasters';
import { ContollingService } from '../ControllingService';
import { Row } from 'react-bootstrap';
import { ControllingCard } from './ControllingCard';
import { ILongTermIncomingInvoice } from '../../../model/controlling/Controlling';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { LongTermIncomingInvoicesFilter } from './LongTermIncomingInvoicesFilter';
import { formatDate } from '../../../utils/Functions';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
export interface ILongTermIncomingInvoiceStatistics {
    missingInvoices: number;
    openInvoices: number;
    upcomingInvoices: number;
}
const LONG_TERM_INVOICES_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Influencer name' },
    { label: 'Agency name' },
    { label: 'Invoice number' },
    { label: 'Campaign name' },
    { label: 'Company name' },
    { label: 'Unlock date' },
    { label: 'Sum of Invoice' },
    { label: 'Status' },
    { label: 'Is allowed upload' },
];
export function LongTermIncomingInvoices() {
    const [dataLoading, setDataLoading] = useState(true);
    const [longTermIncomingInvoices, setLongTermIncomingInvoices] = useState<ILongTermIncomingInvoice[]>([]);
    const [staticts, seuStaticts] = useState<ILongTermIncomingInvoiceStatistics | null>(null);
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 15,
        id: '',
        totalPages: 0,
        campaignId: undefined,
        agencyId: undefined,
        influencerId: undefined,
    });

    const [statsLoading, setStatsLoading] = useState(true);
    const requestIdRef = React.useRef(0);

    const fetchIncomingDeferrals = (params: typeof filters, pageOverride?: number) => {
        const requestId = ++requestIdRef.current;
        setDataLoading(true);
        if (pageOverride === undefined) setStatsLoading(true);
        const page = pageOverride ?? params.page;

        ContollingService.getLongTermIncomingInvoices({ ...params, page } as any)
            .then(({ data, page: responsePage, count, info }: any) => {
                if (requestId !== requestIdRef.current) return;

                const totalPages = Math.ceil(count / params.perPage);
                if (totalPages > 0 && page > totalPages) {
                    fetchIncomingDeferrals({ ...params, page: 1 });
                    return;
                }

                setLongTermIncomingInvoices(data);
                seuStaticts(info);
                setFilters(prev => ({
                    ...prev,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setDataLoading(false);
                    setStatsLoading(false);
                }
            });
    };

    const handlePageChange = (page: number) => {
        fetchIncomingDeferrals(filters, page);
    };

    const renderAllowedStatus = (v: boolean) => (
        <span className={`word-break ${v ? 'text-primary' : 'text-danger'}`}>{v ? <FaCheck /> : <FaTimes />}</span>
    );

    const renderStatusText = (status: string | null) => {
        switch (status) {
            case null:
                return <span className="text-warning">Upcoming</span>;

            case 'missing':
                return <span className="text-danger">Missing</span>;

            default:
                return <span className="text-primary">Open</span>;
        }
    };
    useEffect(() => {
        setFilters(prev => ({ ...prev, page: 1 }));
        fetchIncomingDeferrals({ ...filters, page: 1 });
    }, []);
    return (
        <>
            <div className="mb-5">
                <Row className="gap-y-4">
                    <ControllingCard
                        cardName="Missing Invoices"
                        icon={<FaFileInvoice />}
                        baseColor="danger"
                        total={staticts?.missingInvoices ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                    <ControllingCard
                        cardName="Open Invoices"
                        icon={<FaHourglassStart />}
                        baseColor="primary"
                        total={staticts?.openInvoices ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                    <ControllingCard
                        cardName="Upcoming Invoices (Next Month)"
                        icon={<FaClock />}
                        baseColor="warning"
                        total={staticts?.upcomingInvoices ?? 0}
                        styleCard="h-100"
                        isLoading={statsLoading}
                    />
                </Row>
            </div>

            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') fetchIncomingDeferrals(filters);
                }}
            >
                <LongTermIncomingInvoicesFilter
                    handleState={fetchIncomingDeferrals}
                    setState={setFilters}
                    offersFilter={filters}
                />
            </Card>

            <TableLayout
                pagination={filters}
                listLength={longTermIncomingInvoices.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'invoices'}
                skeletonColumns={LONG_TERM_INVOICES_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {LONG_TERM_INVOICES_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {longTermIncomingInvoices.map(invoice => (
                            <tr key={invoice.id}>
                                <td>{invoice.id}</td>
                                <td>
                                    <ImpersonateEye id={invoice?.contract?.user?.id ?? 0} />
                                    {invoice?.contract?.user?.displayName}
                                    <br />
                                    {invoice?.contract?.user?.firstName ?? '-'} {invoice?.contract?.user?.lastName}
                                </td>
                                <td>
                                    {invoice?.contract?.user?.agencyCompany?.displayName ? (
                                        <span className="word-break">
                                            <span className="text-primary">
                                                ({invoice?.contract?.user?.agencyCompany?.id})
                                            </span>{' '}
                                            {invoice?.contract?.user?.agencyCompany?.displayName}
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {invoice.sequenceNumber ?? 0}/{invoice.contract?.possibleInvoices ?? 0}
                                </td>
                                <td>
                                    {invoice?.contract?.campaign?.title ? (
                                        <>
                                            <span className="text-primary">({invoice?.contract?.campaign.id})</span>{' '}
                                            {invoice?.contract?.campaign.title}
                                        </>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {invoice?.contract?.campaign?.company ? (
                                        <>
                                            <span className="text-primary">
                                                ({invoice?.contract?.campaign.company?.id})
                                            </span>{' '}
                                            {invoice.contract?.campaign?.company?.displayName}
                                        </>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td>{formatDate(invoice.dueDate)}</td>
                                <td>{priceSplitter(invoice.price ?? 0)} €</td>
                                <td>{renderStatusText(invoice?.status)}</td>
                                <td>{renderAllowedStatus(invoice?.isAllowed)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
